/**
 * "Why we ask" info modal, opened from the small teal link under a
 * follow-up question's subtitle. Unlike HealthProtectedModal's static
 * points, the body is admin-authored HTML (`recipe_answer_translations
 * .why_we_ask_block`) rendered as-is, since content is fully bespoke
 * per question.
 */

import React from "react";
import PrimaryCtaButton from "./PrimaryCtaButton";
import VariantBModalShell, { ModalCloseButton } from "./VariantBModalShell";
import { useShellId } from "./shellContext";
import * as translator from "../../../utils/translator";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

interface WhyWeAskModalProps {
  isOpen: boolean;
  onClose: () => void;
  bodyHtml?: string;
  language?: string;
}

const MODAL_ID = "wl2_why_we_ask_modal";

const WhyWeAskModal: React.FC<WhyWeAskModalProps> = ({ isOpen, onClose, bodyHtml, language }) => {
  const cardId = useShellId(MODAL_ID);
  return (
    <VariantBModalShell
      modalId={MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      mobileBottomSheet
      floatingSheet
      maxWidthPx={560}
      bare
    >
      <div className="flex h-[69px] items-center justify-between border-b border-[#f3f4f6] pl-6 pr-4 lg:h-auto lg:border-0 lg:px-[30px] lg:pt-[30px]">
        <h2 className="m-0 font-['Poppins'] text-[20px] font-medium leading-[1.3] text-[#08080a] lg:font-semibold">
          {t(language, "why_we_ask_modal_title")}
        </h2>
        <ModalCloseButton
          cardId={cardId}
          onClose={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] lg:h-5 lg:w-5"
        />
      </div>

      <div
        className="px-6 pt-5 lg:px-[30px] lg:pt-[18px]"
        dangerouslySetInnerHTML={{ __html: bodyHtml || "" }}
      />

      <div className="mt-5 border-t border-[#f3f4f6] px-6 pb-4 pt-[17px] lg:mt-0 lg:border-0 lg:px-[30px] lg:pb-[30px] lg:pt-[18px]">
        <PrimaryCtaButton
          id="wl2_why_we_ask_continue"
          label={t(language, "why_we_ask_modal_cta")}
          onClick={onClose}
          className="!h-12 !rounded-[14px] lg:!h-11 lg:!rounded-[10px] lg:!text-[14px]"
        />
      </div>
    </VariantBModalShell>
  );
};

export default WhyWeAskModal;
